import { useOrganization } from '@/lib/OrganizationContext';
import { useSession } from '@/lib/AuthContext';
import { hasActionPermission, hasPageAccess, type Role } from '@/lib/permissionsConfig';

export function usePermissions() {
  const { hasPermission, currentOrganization, userMemberships } = useOrganization();
  const { session } = useSession();

  const getCurrentUserRole = (): Role | null => {
    if (!currentOrganization) return null;

    if (session?.user?.is_super_admin) {
      return 'owner';
    }

    const membership = userMemberships.find((m) => m.organization_id === currentOrganization.id);

    return (membership?.role as Role) || null;
  };

  const canCreate = (resource: string) => {
    const role = getCurrentUserRole();
    return role ? hasActionPermission(resource, 'create', role) : false;
  };

  const canUpdate = (resource: string) => {
    const role = getCurrentUserRole();
    return role ? hasActionPermission(resource, 'update', role) : false;
  };

  const canDelete = (resource: string) => {
    const role = getCurrentUserRole();
    return role ? hasActionPermission(resource, 'delete', role) : false;
  };

  const canView = (resource: string) => {
    const role = getCurrentUserRole();
    return role ? hasActionPermission(resource, 'view', role) : false;
  };

  const canAccessPage = (path: string) => {
    const role = getCurrentUserRole();
    return role ? hasPageAccess(path, role) : false;
  };

  const isOwner = () => getCurrentUserRole() === 'owner';
  const isAdmin = () => {
    const role = getCurrentUserRole();
    return role === 'owner' || role === 'admin';
  };
  const isMember = () => {
    const role = getCurrentUserRole();
    return (
      role === 'owner' ||
      role === 'admin' ||
      role === 'member' ||
      role === 'coach' ||
      role === 'player'
    );
  };
  const isCoach = () => getCurrentUserRole() === 'coach';
  const isPlayer = () => getCurrentUserRole() === 'player';

  return {
    hasPermission,
    getCurrentUserRole,
    canCreate,
    canUpdate,
    canDelete,
    canView,
    canAccessPage,
    isOwner,
    isAdmin,
    isMember,
    isCoach,
    isPlayer,
  };
}
